import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { SERIES_STEPS, markRoles, type SchmancyMarkColor, type SchmancyTone } from '@schmancy/theme/tone'
import { css, html, type PropertyValues } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { easeOutCubic } from './curves'
import '@schmancy/primitives/tone-dot'
import '@schmancy/primitives/typography'

/** One member of a stacked row. `value` is absolute; its width is derived
 *  against the row's own total, so a percentage is never passed in. */
export interface PillSegment {
	/** Cross-row identity — the same category in two rows is ONE colour and one
	 *  legend entry. Falls back to `label`, which is what identifies a category
	 *  whenever the consumer has nothing steadier to key it by. */
	id?: string
	label: string
	value: number
	/**
	 * Pins this segment to a semantic tone instead of its place in the theme's
	 * categorical scale. Reach for it only where the segment genuinely reports a
	 * state (a refusal, a success); a categorical split — which product, which
	 * channel — wants the scale, whose members are peers. Same closed vocabulary
	 * `<schmancy-segmented-bar>` takes, so one panel's two shapes never disagree
	 * about what a colour means.
	 */
	color?: SchmancyTone
}

/** One ranked row: a named bucket and the figure it holds. */
export interface PillDataPoint {
	/** Stable identity for the keyed render; falls back to the index — the same
	 *  contract `ChartPoint` states, so two rows may legitimately share a label. */
	id?: string | number
	label: string
	/** The figure the bar's width is a share of the widest row's. */
	value: number
	/** Splits this row's own value into named parts. Every category across every
	 *  row shares one colour and one legend entry. */
	segments?: PillSegment[]
	/**
	 * The row being singled out — the winner, or the setting in force. Painted
	 * in the success tone on its own tinted ground, set in bold, and marked
	 * `aria-current` so the singling-out survives colour being unavailable.
	 *
	 * The only per-row emphasis there is, and deliberately: a row that is merely
	 * LOW is already the short bar, so a second channel saying so re-encodes the
	 * geometry. A row that has to be set back for a reason the width
	 * does not carry wants a channel that is not colour.
	 */
	isPeak?: boolean
}

/** Gap between two adjacent segments of one stacked row, in px. Constant, so it
 *  comes out of the free space before any of it is distributed and every
 *  segment stays exactly proportional to its own value.
 *
 *  It deliberately matches `<schmancy-segmented-bar>`'s spacer, for that
 *  primitive's reason: a stacked row is a row of MEMBERS, and a gap is what
 *  tells one member from the next where two of them share a tone.
 *  `<schmancy-funnel>` answers the same question the other way with no spacer
 *  at all, because its claim is one continuous silhouette — so this number is a
 *  per-shape decision that happens to agree, not one fact written twice. */
const SEGMENT_GAP_PX = 2

/** Floor on a NON-ZERO bar's own width, as a percentage of the track. A row
 *  holding 0.2 % of the widest still has to be a bar an eye can find; below
 *  this the shape stops reporting that the row has anything at all. A row
 *  holding nothing is not floored — its own figure reads "0", and a bar beside
 *  that figure is the shape contradicting it. */
const MIN_BAR_PCT = 2

/** A category resolved once for the whole chart: the colour its every segment
 *  paints in, and the legend row that names it. */
interface PillCategory {
	key: string
	label: string
	color: SchmancyMarkColor
}

/** One drawn part of a stacked row — the segment and the colour it resolved to,
 *  so the track and the legend's own mark can never drift apart. */
interface PillPart {
	segment: PillSegment
	key: string
	color: SchmancyMarkColor
}

export interface SchmancyRowActivateDetail {
	id: string | number
	row: PillDataPoint
	index: number
	originalEvent: MouseEvent
}

export type SchmancyRowActivateEvent = CustomEvent<SchmancyRowActivateDetail>

/**
 * Ranked categorical rows: one horizontal bar per named bucket, each bar's
 * width its share of the widest row, so the ordering and the magnitudes read at
 * a glance rather than as a column of figures. The bar shape for a distribution
 * across names — a leaderboard, the top days, what one figure becomes at every
 * setting of the knob that shapes it. A series over a continuous x-axis is one
 * of the XY shapes in this folder instead.
 *
 * THE COLUMNS ARE THE CHART. Label, track and figure sit in one grid the rows
 * share through `grid-cols-subgrid`, so every label right-aligns to one edge and
 * every figure left-aligns to another with nothing measured and no width prop to
 * tune — a bar whose left edge moved row to row would encode a difference that
 * is not in the data. The label column wraps rather than truncating, so a phone
 * loses width from the track and never a name.
 *
 * A row's own value is PAINTED as a figure beside its bar, not revealed on
 * hover: a figure a pointer has to earn is a figure a touch reader never gets.
 * Each row is its own `role="img"` whose name carries the label, the figure and
 * — where the row is stacked — every segment's own name and figure, since a
 * segment is otherwise reachable only by looking at it. The legend beneath is
 * hidden from assistive technology as the duplicate it then is.
 *
 * A stacked row's colours come from the theme's categorical series scale, keyed
 * to a category's FIRST APPEARANCE across `data` rather than to its rank, so a
 * category that empties and refills keeps its colour instead of recolouring
 * every sibling beside it. A segment that genuinely reports a state pins its own
 * tone through `color` and consumes no step of the scale.
 */
@schmancyElement('schmancy-pills')
export class SchmancyPills extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	]

	/** The rows, in the order they should read. This shape ranks nothing itself:
	 *  the order on screen is the order given. */
	@property({ type: Array }) data: PillDataPoint[] = []

	/** Printed before every figure — a currency mark, a tilde. */
	@property({ type: String }) valuePrefix = ''

	/** Printed after every figure — a unit, a percent sign. */
	@property({ type: String }) valueSuffix = ''

	/** Fraction digits on the printed figure. Ignored where `valueFormatter` is
	 *  set — a caller that owns the whole rendering owns its precision with it. */
	@property({ type: Number }) valueDecimals = 2

	/**
	 * Formats every absolute figure the chart prints, the same hook
	 * `<schmancy-funnel>` and `<schmancy-segmented-bar>` take. Without it the
	 * figure is `toFixed(valueDecimals)`, which is a bare decimal — so a surface
	 * that prints 18.940 everywhere else prints 18940.00 beside this chart, and
	 * there is no locale a `valuePrefix`/`valueSuffix` pair can put back.
	 * `valuePrefix`/`valueSuffix` still wrap whatever this returns.
	 */
	@property({ attribute: false }) valueFormatter?: (value: number) => string

	/** Renders the swatch/label key beneath the rows, naming the categories a
	 *  stacked row is split into. Nothing to key on an unstacked chart, so it is
	 *  stood down there regardless of this flag. */
	@property({ type: Boolean }) showLegend = true

	/** Track height in px, rounded to a whole pixel — a fractional track leaves a
	 *  hairline of the ground showing along one edge of every row. */
	@property({ type: Number }) thickness = 24

	/** Makes each named row an opt-in filter control. */
	@property({ type: Boolean }) selectable = false

	/** Controlled identity of the selected row. */
	@property({ attribute: false }) selectedId: string | number | null = null

	/**
	 * Grow the bars from nothing the first time the chart scrolls into view.
	 * Leaving the viewport re-arms it, and a `data` change replays it.
	 *
	 * Off by default, which is the only default a boolean attribute can express:
	 * `?animated=${false}` REMOVES the attribute, and an attribute that was never
	 * present cannot turn a property off — so a default of `true` leaves every
	 * consumer that writes the idiomatic opt-out animating anyway, and a chart
	 * mounted where the observer never sees it (below the fold, inside a drill
	 * panel that opens scrolled) paints every bar at zero width and stays there.
	 */
	@property({ type: Boolean }) animated = false

	/** How long that growth takes, in ms. */
	@property({ type: Number }) animationDuration = 500

	@state() private animationProgress = 0

	/** Plain field, not `@state` — never read in `render()`; `animationProgress`
	 *  already drives the re-render this bookkeeping needs. */
	private visible = false

	private observer: IntersectionObserver | null = null
	private animationFrame: number | null = null

	connectedCallback() {
		super.connectedCallback()
		// Guarded because schmancy ships as a library whose modules can be
		// imported without a DOM present, where `IntersectionObserver` may not
		// exist. With no observer the chart settles at its full width on first
		// paint rather than staying blank forever.
		if (typeof IntersectionObserver === 'undefined') {
			this.animationProgress = 1
			return
		}
		this.observer = new IntersectionObserver(
			entries => {
				const entry = entries[0]
				if (!entry) return
				if (entry.isIntersecting && !this.visible) {
					this.visible = true
					if (this.animated) this.startAnimation()
					else this.animationProgress = 1
				} else if (!entry.isIntersecting) {
					this.visible = false
					if (this.animationFrame !== null) {
						cancelAnimationFrame(this.animationFrame)
						this.animationFrame = null
					}
				}
			},
			{ threshold: 0.1 },
		)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame)
		this.animationFrame = null
		this.observer?.disconnect()
		this.observer = null
	}

	protected willUpdate(changed: PropertyValues) {
		super.willUpdate(changed)
		// A chart that is not animating is settled from its FIRST paint and never
		// waits on the observer — an entrance nobody asked for is not a reason to
		// leave the bars at zero width until something scrolls.
		if (!this.animated) {
			this.animationProgress = 1
			return
		}
		// From here and never `updated()`: `animationProgress` is reactive, so a
		// reset written after render schedules a trailing update (Lit's
		// `change-in-update` warning on every data change); written here it folds
		// into the update already in flight.
		if (changed.has('data') && this.visible) {
			this.animationProgress = 0
			this.startAnimation()
		}
	}

	protected updated(changed: PropertyValues): void {
		super.updated(changed)
		this.observer?.observe(this)
	}

	private startAnimation(): void {
		const startTime = performance.now()
		const animateFrame = (currentTime: number) => {
			// Clamped low as well as high: a rAF callback's timestamp is the
			// frame's START time, which can predate the `performance.now()` read
			// above, and a negative progress paints a negative width.
			const progress = Math.min(Math.max((currentTime - startTime) / this.animationDuration, 0), 1)
			this.animationProgress = easeOutCubic(progress)
			if (progress < 1 && this.visible) this.animationFrame = requestAnimationFrame(animateFrame)
		}
		this.animationFrame = requestAnimationFrame(animateFrame)
	}

	private formatValue(value: number): string {
		return `${this.valuePrefix}${this.valueFormatter?.(value) ?? value.toFixed(this.valueDecimals)}${this.valueSuffix}`
	}

	render() {
		// Every category across every row, in FIRST-APPEARANCE order — the
		// category's own place in the data rather than its rank, so a poll where
		// one category outgrows another repaints nothing. A category pinned to a
		// tone consumes no step of the categorical scale, leaving the scale's
		// separation for the members that actually need telling apart.
		const categories = new Map<string, PillCategory>()
		let step = 0
		for (const row of this.data)
			for (const segment of row.segments ?? []) {
				const key = segment.id ?? segment.label
				if (categories.has(key)) continue
				categories.set(key, { key, label: segment.label, color: segment.color ?? step++ })
			}
		if (import.meta.env.DEV && step > SERIES_STEPS)
			console.warn(
				`<schmancy-pills>: ${step} categories share a ${SERIES_STEPS}-step categorical scale, so two of them paint the same colour. Fold the tail into one named rest category before it reaches the chart.`,
				this.data,
			)
		// A chart whose every row holds nothing still draws its tracks: an empty
		// distribution is an answer, and a collapsed row would reflow the surface
		// around it the moment the first figure arrives.
		const widest = Math.max(...this.data.map(row => row.value), 0)
		const thickness = Math.round(this.thickness)

		return html`
			<div part="rows" class="@container grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
				${repeat(
					this.data,
					(row, index) => row.id ?? index,
					(row, rowIndex) => {
						const rowId = row.id ?? rowIndex
						const isSelected = this.selectable && this.selectedId === rowId
						const settled = row.value > 0 && widest > 0 ? Math.max(MIN_BAR_PCT, (row.value / widest) * 100) : 0
						const parts: PillPart[] = (row.segments ?? [])
							.filter(segment => segment.value > 0)
							.map(segment => {
								const key = segment.id ?? segment.label
								return { segment, key, color: categories.get(key)?.color ?? 'primary' }
							})

						const rowContent = html`
							<!-- A CONTAINER query, not a viewport one: the case this chart is
								     built for is a 390px drill panel on a 1440px page, where a
								     viewport breakpoint would hand the label column 176px and take
								     it straight out of the track. -->
							<schmancy-typography
								part="label"
								type="label"
								token="md"
								weight=${ifDefined(row.isPeak ? 'bold' : undefined)}
								class=${this.classMap({
									'max-w-24 text-right hyphens-auto break-words @md:max-w-44': true,
									'text-success-on-container': !!row.isPeak,
								})}
								>${row.label}</schmancy-typography
							>

							<div
								part="track"
								class="w-full overflow-hidden rounded-full bg-surface-containerHighest"
								style=${this.styleMap({ height: `${thickness}px` })}
							>
								${when(
									parts.length === 0,
									() =>
										html`<div
											part="bar"
											class="h-full rounded-full"
											style=${this.styleMap({
													width: `${settled * this.animationProgress}%`,
													backgroundColor: markRoles(row.isPeak ? 'success' : 'primary').ink,
												})}
										></div>`,
									() =>
										html`<div
											part="bar"
											class="flex h-full"
											style=${this.styleMap({
													width: `${settled * this.animationProgress}%`,
													gap: `${SEGMENT_GAP_PX}px`,
												})}
										>
											${repeat(
													parts,
													part => part.key,
													part =>
														html`<div
															part="segment"
															class="h-full min-w-0 first:rounded-l-full last:rounded-r-full"
															style=${this.styleMap({
															flexGrow: String(part.segment.value),
															flexBasis: '0',
															backgroundColor: markRoles(part.color).ink,
														})}
														></div>`,
												)}
										</div>`,
								)}
							</div>

							<schmancy-typography
								part="value"
								type="label"
								token="md"
								weight=${ifDefined(row.isPeak ? 'bold' : undefined)}
								class=${this.classMap({
									'tabular-nums': true,
									'text-success-on-container': !!row.isPeak,
								})}
								>${this.formatValue(row.value)}</schmancy-typography
							>
						`

						return when(
							this.selectable,
							() => html`
								<button
									part="row"
									type="button"
									aria-label=${[
										`${row.label}: ${this.formatValue(row.value)}`,
										...parts.map(part => `${part.segment.label}: ${this.formatValue(part.segment.value)}`),
									].join(', ')}
									aria-current=${ifDefined(row.isPeak ? 'true' : undefined)}
									aria-pressed=${isSelected ? 'true' : 'false'}
									class=${this.classMap({
										'col-span-3 grid grid-cols-subgrid items-center appearance-none border-0 text-left px-2 py-2 rounded-lg': true,
										'bg-transparent': !row.isPeak && !isSelected,
										'bg-success-container': !!row.isPeak && !isSelected,
										'bg-secondary-container ring-2 ring-secondary-default': isSelected,
										'cursor-pointer focus:outline-solid focus:outline-2 focus:outline-offset-0 focus:outline-secondary-default': true,
									})}
									@click=${(event: MouseEvent) => {
											const detail: SchmancyRowActivateDetail = {
												id: rowId,
												row,
												index: rowIndex,
												originalEvent: event,
											}
											this.dispatchEvent(
												new CustomEvent<SchmancyRowActivateDetail>('row-activate', {
													detail,
													bubbles: true,
													composed: true,
												}),
											)
										}}
									@keydown=${(event: KeyboardEvent) => {
																if (event.key !== 'Enter' && event.key !== ' ') return
																	event.preventDefault()
																if (event.currentTarget instanceof HTMLButtonElement)
																	event.currentTarget.click()
										}}
								>
									${rowContent}
								</button>
							`,
							() => html`
								<div
									part="row"
									role="img"
									aria-label=${[
										`${row.label}: ${this.formatValue(row.value)}`,
										...parts.map(part => `${part.segment.label}: ${this.formatValue(part.segment.value)}`),
									].join(', ')}
									aria-current=${ifDefined(row.isPeak ? 'true' : undefined)}
									class=${this.classMap({
										'col-span-3 grid grid-cols-subgrid items-center px-2 py-2 rounded-lg': true,
										'bg-success-container': !!row.isPeak,
									})}
								>
									${rowContent}
								</div>
							`,
						)
					},
				)}
			</div>

			${when(
				this.showLegend && categories.size > 0,
				() => html`
					<div
						part="legend"
						class="mt-4 grid items-center gap-x-4 gap-y-1 border-t border-outline-variant pt-4 grid-cols-[repeat(auto-fill,minmax(140px,1fr))]"
						aria-hidden="true"
					>
						${repeat(
							[...categories.values()],
							category => category.key,
							category => html`
								<div class="flex min-w-0 items-center gap-1.5">
									<schmancy-tone-dot class="h-2.5 w-2.5" .color=${category.color} variant="solid"></schmancy-tone-dot>
									<schmancy-typography type="label" token="sm" class="min-w-0 truncate text-surface-on-variant"
										>${category.label}</schmancy-typography
									>
								</div>
							`,
						)}
					</div>
				`,
			)}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-pills': SchmancyPills
	}
}
